import React, { ChangeEvent } from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@mui/material';
import { Container } from '../../../../components/container';
import { Row } from '../../../../components/row';
import { Cell } from '../../../../components/cell';
import { MultiSelect } from '../../../../components/multi-select';
import { DatePicker, DatePickerValue } from '../../../../components/date-picker';
import { AttendanceStatisticsCourse, AttendanceStatisticsErrors, AttendanceStatisticsState } from '../interfaces';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../components/accordion';
import { Button } from '../../../../components/button';

const ALL_COURSES_OPTION_ID = 'all-courses';
const ALL_COURSES_OPTION: AttendanceStatisticsCourse = {
  courseId: ALL_COURSES_OPTION_ID,
  name: 'Alle fremmødeforløb',
};

function isAllCoursesOption(option: AttendanceStatisticsCourse) {
  return option.courseId === ALL_COURSES_OPTION_ID;
}

interface IAttendanceStatisticsFilterProps {
  isLoading: boolean;
  courses: AttendanceStatisticsCourse[];
  selectedCourses?: AttendanceStatisticsCourse[];
  fromDate: DatePickerValue;
  toDate: DatePickerValue;
  includeFutureLessons: boolean;

  onAttendanceFilterChange: <K extends keyof AttendanceStatisticsState>(name: K, value: AttendanceStatisticsState[K]) => void;

  errors: AttendanceStatisticsErrors;

  onShowStatistics: () => void;
  onReset: () => void;
  showCourseFilter: boolean;
}

export function AttendanceStatisticsFilter(props: IAttendanceStatisticsFilterProps) {
  const { isLoading, courses, selectedCourses, fromDate, toDate, includeFutureLessons, onAttendanceFilterChange, errors, onShowStatistics, onReset, showCourseFilter } = props;

  function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    onAttendanceFilterChange('includeFutureLessons', checked);
  }

  function handleCourseSelectionChange(value: AttendanceStatisticsCourse[]) {
    onAttendanceFilterChange('selectedCourses', value.some(isAllCoursesOption) ? courses : value);
  }

  const hasSelectedCourses = (selectedCourses?.length || 0) > 0;
  const courseOptions = courses.length > 0 ? [ALL_COURSES_OPTION, ...courses] : [];

  return (
    <Accordion defaultExpanded>
      <AccordionSummary>Fremmødefiltrering</AccordionSummary>
      <AccordionDetails>
        <Container>
          {showCourseFilter && (
            <Row>
              <Cell size="full">
                <MultiSelect<AttendanceStatisticsCourse>
                  label="Hold"
                  getOptionLabel={x => x.name}
                  onChange={handleCourseSelectionChange}
                  options={courseOptions}
                  value={selectedCourses}
                  variant="outlined"
                  isOptionEqualToValue={(option, value) => option.courseId === value.courseId}
                  renderOption={(prop: object, option: AttendanceStatisticsCourse) => (
                    <li {...prop} key={option.courseId} style={isAllCoursesOption(option) ? { fontStyle: 'italic' } : undefined}>
                      {option.name}
                    </li>
                  )}
                  description={`Der er valgt ${selectedCourses?.length || 0} ud af ${courses.length} fremmødeforløb`}
                />
              </Cell>
            </Row>
          )}
          <Row>
            <Cell size="half">
              <DatePicker
                required
                label="Startdato"
                fullWidth
                variant="outlined"
                onChange={(date: DatePickerValue) => onAttendanceFilterChange('fromDate', date)}
                value={fromDate}
                error={!!errors.fromDate}
                errorText={errors.fromDate}
              />
            </Cell>
            <Cell size="half">
              <DatePicker
                required
                label="Slutdato"
                fullWidth
                variant="outlined"
                onChange={(date: DatePickerValue) => onAttendanceFilterChange('toDate', date)}
                value={toDate}
                error={!!errors.toDate}
                errorText={errors.toDate}
              />
            </Cell>
          </Row>
          <Row>
            <Cell size="full">
              <FormControl component="fieldset" variant="standard">
                <FormLabel component="legend">Medtag alle lektioner herunder ikke registrerede/godkendte</FormLabel>
                <FormGroup>
                  <Box>
                    <FormControlLabel
                      control={<Checkbox name="includeFutureLessons" onChange={handleCheckboxChange} checked={includeFutureLessons} />}
                      label="Medtag alle lektioner"
                    />
                  </Box>
                </FormGroup>
              </FormControl>
            </Cell>
          </Row>
          <Row>
            <Cell size="full">
              <Box sx={{ display: 'flex', justifyContent: 'right', gap: 1 }}>
                <Button color="primary" onClick={onShowStatistics} variant="contained" loading={isLoading} disabled={showCourseFilter && !hasSelectedCourses}>
                  Vis
                </Button>
                <Button onClick={onReset} variant="outlined">
                  Nulstil
                </Button>
              </Box>
            </Cell>
          </Row>
        </Container>
      </AccordionDetails>
    </Accordion>
  );
}
